import { Button, ColumnTable, Icon } from '@octano/global-ui';
import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDate } from '../../../utils/dates';
import { DegreeProcessRendition, DegreeProcessRenditionRow } from '../types';
import BadgeState from './BadgeState';

interface Props {
  toggleModalRendition: (
    activityId: number | string,
    editRendition?: DegreeProcessRendition,
  ) => void;
  canEdit: boolean;
  toggleDeleteModal: (editRendition?: DegreeProcessRendition) => void;
}

export function useColumnsRenditions({
  toggleModalRendition,
  canEdit = false,
  toggleDeleteModal,
}: Props) {
  const prefix = 'studentsDegreeStudent.tableRenditions';
  const { t } = useTranslation();
  const texts = useMemo(
    () => ({
      headers: t<string, Record<string, string>>(`${prefix}.headers`, {
        returnObjects: true,
      }),
      badgeState: t<string, Record<'approved' | 'failed', string>>(
        `${prefix}.badgeState`,
        {
          returnObjects: true,
        },
      ),
    }),
    [t],
  );

  const columns = useCallback(
    (currentId?: number): ColumnTable<DegreeProcessRenditionRow>[] => {
      const columnWidth = `${100 / 7}%`;
      return [
        {
          columnName: 'date',
          headerText: 'Fecha de Rendición',
          thClassName: 'text-center',
          tdClassName: 'text-center',
          width: columnWidth,
          cellFormat: ({ row }) =>
            row?.buildEmptyRow ? <></> : formatDate(row.date, 'DD/MM/YYYY'),
        },
        {
          columnName: 'state',
          headerText: 'Estado',
          thClassName: 'text-center',
          tdClassName: 'text-center',
          width: columnWidth,
          cellFormat: ({ row }) => {
            if (row?.buildEmptyRow) {
              return <></>;
            }
            if (row.approved) {
              return (
                <BadgeState
                  status="approved"
                  text={texts.badgeState.approved}
                />
              );
            }
            return (
              <BadgeState status="failed" text={texts.badgeState.failed} />
            );
          },
        },
        {
          columnName: 'grade',
          headerText: 'Nota',
          thClassName: 'text-center',
          tdClassName: 'text-center',
          width: columnWidth,
          cellFormat: ({ row }) => (row?.buildEmptyRow ? <></> : row.grade),
        },
        {
          columnName: 'gradeMax',
          headerText: 'Nota Máxima',
          thClassName: 'text-center',
          tdClassName: 'text-center',
          width: columnWidth,
          cellFormat: ({ row }) => (row?.buildEmptyRow ? <></> : row.gradeMax),
        },
        {
          columnName: 'comment',
          headerText: 'Comentario',
          thClassName: 'text-center',
          tdClassName: 'text-center',
          width: columnWidth,
          cellFormat: ({ row }) => (row?.buildEmptyRow ? <></> : row.comment),
        },
        {
          columnName: 'creator',
          headerText: 'Editado por',
          thClassName: 'text-center',
          tdClassName: 'text-center',
          width: columnWidth,
          cellFormat: ({ row }) =>
            row?.buildEmptyRow ? <></> : row.creator.fullName,
        },
        {
          columnName: 'actions',
          headerText: 'Acciones',
          tdClassName: 'text-center',
          thClassName: 'text-center',
          width: columnWidth,
          cellFormat: ({ row }) => {
            return row?.buildEmptyRow ? (
              <></>
            ) : (
              <>
                <button
                  className="btn"
                  onClick={() => toggleModalRendition(row.activityId, row)}
                  disabled={!canEdit || currentId !== row.id}
                >
                  <Icon
                    name="edit"
                    color={
                      !canEdit || currentId !== row.id ? 'disabled' : 'primary'
                    }
                  />
                </button>
                <button
                  className="btn"
                  onClick={() => toggleDeleteModal(row)}
                  disabled={!canEdit}
                >
                  <Icon
                    name="trash"
                    color={!canEdit ? 'disabled' : 'primary'}
                  />
                </button>
              </>
            );
          },
        },
        {
          columnName: 'buildEmptyRow',
          headerText: '',
          tdClassName: 'text-center td-build-empty-row',
          thClassName: 'text-center',
          width: columnWidth,
          cellFormat: ({ row }) => {
            if (row?.buildEmptyRow) {
              return (
                <>
                  {row.activityId !== 0 ? (
                    <Button
                      className="td-btn-action"
                      text={t(`${prefix}.btn.addRendition`)}
                      onClick={() => {
                        toggleModalRendition(row.activityId);
                      }}
                      disabled={!canEdit || row.hasApprovedRenditions}
                    />
                  ) : (
                    <Button
                      className="td-btn-action"
                      text={t(`${prefix}.btn.addRendition`)}
                      disabled={true}
                    />
                  )}
                </>
              );
            }

            return <></>;
          },
        },
      ];
    },
    [texts, canEdit, t, toggleDeleteModal, toggleModalRendition],
  );

  return columns;
}
